import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useReference } from "@/lib/reference";
import LegalFooter from "@/components/brand/LegalFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sparkles, MessageCircle, Users, HeartHandshake, CheckCircle2, ClipboardList,
  Award, GraduationCap, Target, Compass, Calendar, PlayCircle, ArrowRight,
  Quote, Download, ExternalLink, ShieldCheck, School, Home, Building2,
  BookOpen, Zap, Linkedin, Mail, Phone, Instagram, MessageSquare, Video, MapPin, Tag,
} from "lucide-react";
import { LogoMark, LogoFull } from "@/components/brand/Logo";
import RichText from "@/components/RichText";
import { focusIcon } from "@/lib/focusIcons";

// Joylance's professional headshot — sent by her via chat
const HEADSHOT_URL = "https://customer-assets.emergentagent.com/job_ccs-consult/artifacts/k0vyubd6_image.png";

const BOOKING_SERVICES = [
  "Leadership Development",
  "Communication Excellence",
  "Emotional Intelligence",
  "Conflict Resolution",
  "Team Collaboration & Accountability",
  "Customer Service Excellence",
  "Student Leadership Development",
  "Parent & Community Engagement",
  "Supervisor Development",
  "Strategic Planning Facilitation",
  "Custom Training",
];

const PILLARS = [
  {
    icon: MessageCircle, title: "Communicate", color: "#5D3A1F",
    tagline: "Clear. Confident. Professional.",
    detail: "Communication skills, active listening, professional presence, and the difficult-conversation courage that earns trust in every interaction."
  },
  {
    icon: Users, title: "Collaborate", color: "#C28A2E",
    tagline: "Together. Accountably. Respectfully.",
    detail: "Teamwork, accountability, mutual respect, and the conflict resolution skills that turn friction into forward motion."
  },
  {
    icon: HeartHandshake, title: "Serve", color: "#8B5A2B",
    tagline: "People first. Impact lasting.",
    detail: "Service excellence in every direction — internal service to your team, external service to clients, and lasting relationship building."
  },
];

const SERVICES_GRID = [
  { icon: Compass, title: "Leadership Development", desc: "Grow leaders who inspire, coach, and hold the standard with purpose and integrity." },
  { icon: MessageCircle, title: "Communication Excellence", desc: "Speak, write, and listen with clarity, empathy, and impact." },
  { icon: HeartHandshake, title: "Emotional Intelligence", desc: "Lead yourself well so you can lead others well." },
  { icon: ShieldCheck, title: "Conflict Resolution", desc: "Turn friction into forward motion with practical de-escalation skills." },
  { icon: Users, title: "Team Collaboration & Accountability", desc: "Build teams that own outcomes together." },
  { icon: Award, title: "Customer Service Excellence", desc: "Transform frontline interactions into brand-defining moments." },
  { icon: School, title: "Student Leadership Development", desc: "Equip young people to lead with confidence, purpose, and character." },
  { icon: Home, title: "Parent & Community Engagement", desc: "Strengthen the relationships that shape families and neighbourhoods." },
  { icon: GraduationCap, title: "Supervisor Development", desc: "Equip supervisors to lead confidently and manage performance." },
  { icon: Target, title: "Strategic Planning Facilitation", desc: "Facilitated sessions that align teams around a clear, shared vision." },
  { icon: ClipboardList, title: "Custom Training", desc: "Tailored workshops designed around your specific needs." },
];

const ACHIEVEMENTS = [
  { verb: "Communicate", detail: "with clarity, confidence, and professionalism" },
  { verb: "Build", detail: "stronger relationships through effective communication" },
  { verb: "Lead", detail: "with purpose, integrity, and emotional intelligence" },
  { verb: "Develop", detail: "a service mindset that creates lasting impact" },
  { verb: "Strengthen", detail: "relationships across workplaces, schools, and communities" },
];

const CORE_VALUES = [
  { key: "Communication", desc: "We speak with clarity, honesty, and respect." },
  { key: "Collaboration", desc: "We believe the best results are achieved through teamwork and shared purpose." },
  { key: "Service", desc: "We put people first and strive to create meaningful, lasting impact." },
  { key: "Excellence", desc: "We pursue continuous growth and deliver our very best." },
  { key: "Integrity", desc: "We do what is right, even when no one is watching." },
];

const AUDIENCES = [
  { icon: Building2, label: "Workplaces", color: "#5D3A1F" },
  { icon: School, label: "Schools", color: "#C28A2E" },
  { icon: Home, label: "Communities", color: "#8B5A2B" },
];

// "Now Booking" gets a solid gold fill; "Coming Soon" a gold outline.
const badgeStyle = (badge, onImage) => {
  const b = (badge || "").toLowerCase();
  if (b.includes("now booking")) return "bg-[#C28A2E] text-[#2A1810]";
  if (b.includes("coming soon"))
    return onImage
      ? "bg-transparent border border-[#F5D88A]/80 text-[#F5D88A] backdrop-blur-sm"
      : "bg-transparent border border-[#C28A2E]/60 text-[#9A6B1E]";
  return onImage ? "bg-[#2A1810]/85 text-[#F5D88A] backdrop-blur-sm" : "bg-[#C28A2E]/15 text-[#9A6B1E]";
};

const PublicBookingPage = () => {
  const ref = useReference();
  const [form, setForm] = useState({
    full_name: "", organization_name: "", position: "",
    email: "", phone: "", industry: "", employees_count: "",
    services_interested: [], business_challenge: "",
    preferred_contact: "Email", workshop_interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [siteContent, setSiteContent] = useState({ workshops: [], focus_areas: [] });
  const { hash, search } = useLocation();

  // Enquiries arriving from a workshop page (?workshop=…) are tagged so they
  // can be tracked separately in the admin pipeline.
  useEffect(() => {
    const w = new URLSearchParams(search).get("workshop");
    if (w) setForm((f) => ({ ...f, workshop_interest: w }));
  }, [search]);

  // Admin-editable public content (workshop/event cards + focus area tiles)
  useEffect(() => {
    api.get("/public/site-content").then((r) => setSiteContent(r.data || { workshops: [], focus_areas: [] })).catch(() => {});
  }, []);

  // Scroll to in-page anchors (e.g. /book#booking from the homepage gateway)
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
    }, 150);
    return () => clearTimeout(t);
  }, [hash]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => {
      const list = new Set(f.services_interested);
      if (list.has(s)) list.delete(s); else list.add(s);
      return { ...f, services_interested: Array.from(list) };
    });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name || !form.organization_name || !form.email || !form.business_challenge) {
      setError("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/book-consultation", form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF5] flex items-center justify-center p-6">
        <div className="max-w-xl text-center ccs-fade-up" data-testid="booking-success">
          <div className="w-16 h-16 rounded-2xl bg-[#6B8E5A]/15 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-[#6B8E5A]" strokeWidth={1.7} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Thank you</div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight text-[#2A1810] mt-3">
            Your consultation is on its way
          </h1>
          <p className="text-[#5D3A1F] mt-4 text-base leading-relaxed">
            Joylance will personally review your inquiry and reach out within <strong>1 business day</strong> via your preferred contact method.
          </p>
          <a href="/book" className="inline-block mt-8 text-sm text-[#C28A2E] font-semibold hover:underline" data-testid="booking-new-link">
            Return to home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid="public-booking-page">
      {/* ============== Header ============== */}
      <header className="border-b border-[#EBE3D2] bg-[#FDFBF5]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={44} testid="booking-header-logo" />
            <div className="leading-tight">
              <div className="font-display text-lg font-semibold text-[#2A1810]">The CCS Approach</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B5944] font-medium">Consultancy Services</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="#about" className="hidden md:inline text-sm text-[#6B5944] hover:text-[#5D3A1F]">About</a>
            <a href="#philosophy" className="hidden md:inline text-sm text-[#6B5944] hover:text-[#5D3A1F]">Philosophy</a>
            <a href="#services" className="hidden md:inline text-sm text-[#6B5944] hover:text-[#5D3A1F]">Services</a>
            <Link to="/spotlight" className="hidden md:inline text-sm text-[#5D3A1F] font-semibold hover:text-[#4A2E18]" data-testid="header-spotlight-link">
              Spotlight
            </Link>
            <Link to="/discover" className="hidden md:inline text-sm text-[#5D3A1F] font-semibold hover:text-[#4A2E18]" data-testid="header-discover-link">
              Needs Assessment
            </Link>
            <Link to="/login" className="text-sm text-[#6B5944] hover:text-[#5D3A1F]" data-testid="public-login-link">Sign in</Link>
          </div>
        </div>
      </header>

      {/* ============== Hero ============== */}
      <section className="ccs-grain border-b border-[#EBE3D2]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5D3A1F]/8 text-[#5D3A1F] text-xs font-medium">
              <Sparkles className="w-3 h-3" /> Leadership · Communication · Service Development
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#2A1810] mt-5 leading-[1.05]">
              Empowering people to
              <br />
              <span className="text-[#C28A2E]">communicate clearly, collaborate effectively,</span>
              <br />
              and serve passionately.
            </h1>
            <p className="text-base lg:text-lg text-[#6B5944] mt-6 leading-relaxed max-w-2xl">
              The CCS Approach Consultancy Services equips individuals, teams, and organizations —
              across <strong className="text-[#2A1810]">workplaces, schools, and communities</strong> —
              with practical strategies that build stronger relationships and inspire a lasting culture of service.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/discover" data-testid="hero-discover-btn">
                <Button className="h-12 px-6 rounded-xl bg-[#5D3A1F] hover:bg-[#4A2E18] text-white font-medium text-base hover:-translate-y-[1px] hover:shadow-lg transition-all">
                  <ClipboardList className="w-4 h-4 mr-2" /> Take the Needs Assessment
                </Button>
              </Link>
              <a href="#booking" data-testid="hero-booking-btn">
                <Button variant="outline" className="h-12 px-6 rounded-xl border-[#5D3A1F]/25 text-[#5D3A1F] hover:bg-[#5D3A1F]/5">
                  Book a Consultation <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6">
              {AUDIENCES.map((a) => (
                <div key={a.label} className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${a.color}15` }}>
                    <a.icon className="w-4 h-4" style={{ color: a.color }} strokeWidth={1.8} />
                  </div>
                  <span className="text-sm font-medium text-[#2A1810]">{a.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 flex justify-center">
            <div className="p-4 bg-white rounded-3xl shadow-[0_20px_60px_rgba(45,24,16,0.12)] border border-[#EBE3D2]">
              <LogoFull maxWidth={340} />
            </div>
          </div>
        </div>
      </section>

      {/* ============== About Joylance ============== */}
      <section id="about" className="py-20 lg:py-28 border-b border-[#EBE3D2]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Meet your facilitator</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight text-[#2A1810] mt-3">
              Joylance Chapman-George
            </h2>
            <p className="text-[#5D3A1F] mt-3 text-lg italic">Leadership, Communication &amp; Service Development Facilitator</p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            {/* Photo */}
            <div className="lg:col-span-2">
              <div
                data-testid="about-photo"
                className="relative aspect-[4/5] w-full max-w-sm mx-auto rounded-3xl overflow-hidden border-4 border-[#C28A2E]/40 shadow-[0_20px_60px_rgba(45,24,16,0.25)]"
              >
                <img
                  src={HEADSHOT_URL}
                  alt="Joylance Chapman-George — Facilitator, The CCS Approach"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-4 flex justify-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#C28A2E] font-bold px-2.5 py-1 bg-[#C28A2E]/10 rounded-full">20+ years experience</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#C28A2E] font-bold px-2.5 py-1 bg-[#C28A2E]/10 rounded-full">Trinidad &amp; Tobago</span>
              </div>
              <div className="mt-6 flex justify-center gap-2 flex-wrap">
                <a href="mailto:theccsapproach@gmail.com" className="inline-flex items-center gap-1.5 text-xs text-[#5D3A1F] hover:text-[#4A2E18] font-medium px-3 py-1.5 bg-white border border-[#EBE3D2] rounded-lg" data-testid="about-email-link">
                  <Mail className="w-3.5 h-3.5" /> theccsapproach@gmail.com
                </a>
                <a href="tel:+18687529924" className="inline-flex items-center gap-1.5 text-xs text-[#5D3A1F] hover:text-[#4A2E18] font-medium px-3 py-1.5 bg-white border border-[#EBE3D2] rounded-lg" data-testid="about-phone-link">
                  <Phone className="w-3.5 h-3.5" /> 868 752 9924
                </a>
                <a href="https://linkedin.com/in/joylance-chapman-george-91196b4a" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#5D3A1F] hover:text-[#4A2E18] font-medium px-3 py-1.5 bg-white border border-[#EBE3D2] rounded-lg" data-testid="about-linkedin-link">
                  <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                </a>
              </div>
            </div>

            {/* Bio */}
            <div className="lg:col-span-3">
              <div className="ccs-card ccs-grain p-8 lg:p-10">
                <Quote className="w-8 h-8 text-[#C28A2E]/40" strokeWidth={1.5} />
                <p className="font-display text-xl lg:text-2xl text-[#2A1810] leading-snug mt-2 italic">
                  "Empowering people to communicate clearly, collaborate effectively, and serve passionately."
                </p>
                <div className="mt-8 space-y-4 text-[#5D3A1F] text-base leading-relaxed">
                  <p>
                    Joylance Chapman-George is an experienced facilitator with over <strong>20 years of leadership
                    experience</strong>, dedicated to helping individuals, teams, and organizations strengthen
                    communication, build meaningful relationships, and achieve lasting success through
                    collaboration and service.
                  </p>
                  <p>
                    Through <strong className="text-[#C28A2E]">The CCS Approach Framework</strong>, she equips
                    individuals and organizations with practical strategies that build stronger relationships,
                    foster meaningful collaboration, and inspire a lasting culture of service.
                  </p>
                </div>

                {/* Experience & Qualifications */}
                <div className="mt-8 pt-6 border-t border-[#EBE3D2] grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold mb-3">Experience</div>
                    <div className="border-l-2 border-[#C28A2E]/40 pl-4">
                      <div className="text-sm font-semibold text-[#2A1810]">Senior Leadership</div>
                      <div className="text-xs text-[#6B5944] italic mt-0.5">Financial Services Sector</div>
                      <p className="text-xs text-[#5D3A1F] mt-2 leading-relaxed">
                        Leadership, staff development, operations oversight, and service delivery management.
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold mb-3">Qualifications</div>
                    <ul className="space-y-2 text-sm text-[#5D3A1F]">
                      <li className="flex items-start gap-2"><Award className="w-4 h-4 text-[#C28A2E] mt-0.5 shrink-0" /> MBA, Business Administration</li>
                      <li className="flex items-start gap-2"><Award className="w-4 h-4 text-[#C28A2E] mt-0.5 shrink-0" /> Certified in Business Communication</li>
                      <li className="flex items-start gap-2"><Award className="w-4 h-4 text-[#C28A2E] mt-0.5 shrink-0" /> Certified in Professional Writing &amp; Workplace Documentation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== What I Help People Achieve ============== */}
      <section className="py-20 lg:py-24 bg-white border-b border-[#EBE3D2]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">What Joylance helps people achieve</div>
            <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-[#2A1810] mt-3">
              Practical outcomes people can apply immediately
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            {ACHIEVEMENTS.map((a, i) => (
              <div key={a.verb} className="ccs-card p-5 flex items-start gap-4" data-testid={`achievement-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-[#C28A2E]/15 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4 text-[#C28A2E]" strokeWidth={2} />
                </div>
                <div>
                  <span className="font-display text-lg font-semibold text-[#2A1810]">{a.verb}</span>{" "}
                  <span className="text-[#5D3A1F] text-sm">{a.detail}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 max-w-2xl mx-auto ccs-card ccs-grain p-6 text-center">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Training Style</div>
            <p className="text-[#5D3A1F] mt-2 leading-relaxed italic">
              Practical, relatable, and results-driven — combining real-world experience with interactive
              activities and strategies people can apply immediately at work, school, or in their community.
            </p>
          </div>
        </div>
      </section>

      {/* ============== Philosophy / Mission / Vision / Core Values ============== */}
      <section id="philosophy" className="py-20 lg:py-28 border-b border-[#EBE3D2]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Our philosophy &amp; core values</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight text-[#2A1810] mt-3">
              What we stand for
            </h2>
          </div>

          {/* Mission + Vision */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="ccs-card ccs-grain p-7" data-testid="mission-card">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold px-2.5 py-1 bg-[#C28A2E]/10 rounded inline-block">Mission</div>
              <p className="mt-4 text-[#2A1810] text-lg leading-relaxed font-display italic">
                Empowering people to communicate clearly, collaborate effectively, and serve passionately.
              </p>
            </div>
            <div className="ccs-card ccs-grain p-7" data-testid="vision-card">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold px-2.5 py-1 bg-[#C28A2E]/10 rounded inline-block">Vision</div>
              <p className="mt-4 text-[#2A1810] text-lg leading-relaxed font-display italic">
                To transform lives and workplaces through purposeful communication, meaningful collaboration, and exceptional service.
              </p>
            </div>
          </div>

          {/* Philosophy flow */}
          <div className="mt-14 ccs-card p-8 lg:p-12" data-testid="philosophy-flow">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#C28A2E] font-bold">The CCS Philosophy</div>
              <h3 className="font-display text-2xl lg:text-3xl font-semibold text-[#2A1810] mt-3">One truth, four movements</h3>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              {["Better Communication", "Stronger Relationships", "Better Collaboration", "Exceptional Service"].map((step, i, arr) => (
                <div key={step} className="flex items-center gap-3">
                  <div className="px-5 py-3 rounded-full bg-white border-2 border-[#C28A2E]/40 text-sm font-semibold text-[#2A1810] shadow-sm">
                    {step}
                  </div>
                  {i < arr.length - 1 && <ArrowRight className="w-5 h-5 text-[#C28A2E]" strokeWidth={2} />}
                </div>
              ))}
            </div>
            <p className="text-center text-[#5D3A1F] italic mt-8 max-w-2xl mx-auto leading-relaxed">
              Better communication builds stronger relationships. Stronger relationships create better
              collaboration. Better collaboration produces exceptional service.
            </p>
          </div>

          <p className="text-center text-sm text-[#6B5944] italic mt-12 max-w-2xl mx-auto">
            "Let everything you do be done in love." — 1 Corinthians 16:14
          </p>

          {/* CCS Framework 3 pillars */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <div
                key={p.title}
                className="ccs-card ccs-grain p-7 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(45,24,16,0.15)] transition-all"
                data-testid={`framework-pillar-${p.title.toLowerCase()}`}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${p.color}15` }}>
                  <p.icon className="w-7 h-7" style={{ color: p.color }} strokeWidth={1.6} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: p.color }}>
                  Pillar {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-2xl font-semibold text-[#2A1810] mt-2 tracking-tight">{p.title}</h3>
                <p className="text-sm font-medium text-[#C28A2E] mt-1 italic">{p.tagline}</p>
                <p className="text-[#5D3A1F] mt-4 leading-relaxed text-sm">{p.detail}</p>
              </div>
            ))}
          </div>

          {/* Core Values */}
          <div className="mt-14">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Core Values</div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {CORE_VALUES.map((v) => (
                <div key={v.key} className="ccs-card p-6 border-l-4 border-[#C28A2E]" data-testid={`value-${v.key.toLowerCase()}`}>
                  <h4 className="font-display text-xl font-semibold text-[#2A1810]">{v.key}</h4>
                  <p className="text-sm text-[#5D3A1F] mt-2 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== Video intro placeholder ============== */}
      <section className="py-16 lg:py-20 bg-white border-b border-[#EBE3D2]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">A word from Joylance</div>
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-[#2A1810] mt-3">Hear it in her own voice</h2>
          <div
            data-testid="video-placeholder"
            className="mt-8 aspect-video rounded-2xl bg-gradient-to-br from-[#5D3A1F]/95 to-[#2A1810] flex items-center justify-center border border-[#EBE3D2] cursor-pointer group hover:shadow-[0_20px_60px_rgba(45,24,16,0.25)] transition-all"
          >
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#C28A2E] flex items-center justify-center mx-auto group-hover:scale-110 transition-transform shadow-[0_8px_30px_rgba(194,138,46,0.5)]">
                <PlayCircle className="w-10 h-10 text-white" strokeWidth={1.5} />
              </div>
              <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-[#F5D88A] font-bold">Video placeholder</div>
              <div className="text-[#F0E6D2]/70 text-xs mt-2 italic px-6">
                Record a 60–90 second intro and send it in chat when ready
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Services ============== */}
      <section id="services" className="py-20 lg:py-28 border-b border-[#EBE3D2]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Training focus areas</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight text-[#2A1810] mt-3">
              Training designed for real people
            </h2>
            <p className="text-[#5D3A1F] mt-4 text-lg leading-relaxed">
              Every engagement is customized after a full needs assessment. These are the focus areas we
              facilitate — combine, tailor, or build something new for your workplace, school, or community.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(siteContent.focus_areas.length
              ? siteContent.focus_areas.map((a) => ({ key: a.id, icon: focusIcon(a.icon), title: a.title, desc: a.description }))
              : SERVICES_GRID.map((s) => ({ key: s.title, icon: s.icon, title: s.title, desc: s.desc }))
            ).map((s) => (
              <div
                key={s.key}
                className="ccs-card p-6 hover:border-[#C28A2E]/60 hover:-translate-y-0.5 transition-all"
                data-testid={`service-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#C28A2E]/12 flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-[#C28A2E]" strokeWidth={1.7} />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#2A1810] leading-tight">{s.title}</h3>
                <RichText content={s.desc} className="text-sm text-[#6B5944] mt-2 leading-relaxed" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== Upcoming workshops & events (admin-editable) ============== */}
      <section id="events" className="py-20 lg:py-24 bg-white border-b border-[#EBE3D2]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Upcoming</div>
              <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-[#2A1810] mt-2">
                Public workshops &amp; events
              </h2>
            </div>
            <Link to="/testimonials" className="text-sm text-[#5D3A1F] font-semibold hover:underline flex items-center gap-1" data-testid="testimonials-link">
              See what people are saying <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {siteContent.workshops.length > 0 ? (
            <div
              className={`grid gap-6 ${
                siteContent.workshops.length === 1
                  ? "grid-cols-1 max-w-md mx-auto"
                  : siteContent.workshops.length === 2
                    ? "grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto"
                    : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              }`}
              data-testid="upcoming-workshops-grid"
            >
              {siteContent.workshops.map((w) => (
                <div key={w.id} className="ccs-card overflow-hidden flex flex-col hover:border-[#C28A2E]/60 hover:-translate-y-0.5 transition-all" data-testid={`workshop-card-${w.id}`}>
                  {w.image_url && (
                    <div className="relative">
                      <img src={w.image_url} alt={w.title} className="w-full aspect-[16/9] object-cover" />
                      {w.badge && (
                        <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full ${badgeStyle(w.badge, true)}`}>
                          {w.badge}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    {!w.image_url && w.badge && (
                      <span className={`self-start text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full mb-3 ${badgeStyle(w.badge, false)}`}>
                        {w.badge}
                      </span>
                    )}
                    <h3 className="font-display text-xl font-semibold text-[#2A1810] leading-tight">{w.title}</h3>
                    {w.tagline && <p className="text-sm text-[#C28A2E] font-medium italic mt-1.5">{w.tagline}</p>}
                    {w.description && <RichText content={w.description} className="text-sm text-[#6B5944] mt-3 leading-relaxed" />}
                    <div className="mt-4 space-y-1.5 text-xs text-[#5D3A1F]">
                      {w.date_text && <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-[#C28A2E]" /> {w.date_text}</div>}
                      {w.location && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#C28A2E]" /> {w.location}</div>}
                      {w.price && <div className="flex items-center gap-2"><Tag className="w-3.5 h-3.5 text-[#C28A2E]" /> {w.price}</div>}
                    </div>
                    {w.button_text && (
                      <div className="mt-5 pt-4 border-t border-[#EBE3D2] mt-auto">
                        {w.button_link && /^https?:\/\//i.test(w.button_link) ? (
                          <a href={w.button_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-[#5D3A1F] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#4A2E18] transition-colors" data-testid={`workshop-card-button-${w.id}`}>
                            {w.button_text} <ArrowRight className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          <Link to={w.button_link || "/book#booking"} className="inline-flex items-center gap-1.5 bg-[#5D3A1F] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#4A2E18] transition-colors" data-testid={`workshop-card-button-${w.id}`}>
                            {w.button_text} <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="upcoming-workshops-placeholder">
              {[1, 2, 3].map((n) => (
                <div key={n} className="ccs-card p-6 border-dashed border-2 border-[#EBE3D2]">
                  <Calendar className="w-6 h-6 text-[#C28A2E]/60" strokeWidth={1.6} />
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold mt-4">Coming soon</div>
                  <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">Workshop Title {n}</h3>
                  <p className="text-sm text-[#6B5944] mt-2 italic">Public workshop details will appear here once scheduled.</p>
                  <div className="mt-4 text-xs text-[#C28A2E] font-semibold">Date · Location · Investment</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============== Needs Assessment CTA ============== */}
      <section className="py-20 lg:py-24 ccs-grain border-b border-[#EBE3D2]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C28A2E]/15 text-[#5D3A1F] text-xs font-medium">
            <Sparkles className="w-3 h-3" /> AI-powered proposal generation
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight text-[#2A1810] mt-4">
            Not sure where to start?
            <br />
            <span className="text-[#C28A2E]">Take the Needs Assessment.</span>
          </h2>
          <p className="text-[#5D3A1F] mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
            A guided assessment tailored to your context — workplace, school, or community.
            Your answers help Joylance draft a proposal designed <strong>specifically for you</strong>,
            not a generic template.
          </p>
          <div className="mt-8">
            <Link to="/discover" data-testid="cta-discover-btn">
              <Button className="h-14 px-8 rounded-xl bg-[#5D3A1F] hover:bg-[#4A2E18] text-white font-medium text-base hover:-translate-y-[1px] hover:shadow-lg transition-all">
                <ClipboardList className="w-5 h-5 mr-2" /> Start Needs Assessment (5–10 min)
              </Button>
            </Link>
          </div>
          <p className="text-xs text-[#6B5944] mt-4">No commitment · Fully confidential · Response within 1 business day</p>
        </div>
      </section>

      {/* ============== Booking form ============== */}
      <section id="booking" className="py-20 lg:py-28 border-t border-[#EBE3D2] bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Or, if you prefer</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight text-[#2A1810] mt-3 leading-tight">
              Book a complimentary
              <br />
              <span className="text-[#C28A2E]">30-minute conversation</span>
            </h2>
            <p className="text-[#5D3A1F] mt-5 leading-relaxed">
              A no-pressure call where we listen to your needs and explore practical paths forward.
              No sales pitch — just honest, strategic conversation.
            </p>
            <div className="grid grid-cols-3 gap-3 mt-9 max-w-md">
              {PILLARS.map((p) => (
                <div key={p.title} className="border-l-2 pl-3" style={{ borderColor: p.color }}>
                  <p.icon className="w-4 h-4 mb-1.5" style={{ color: p.color }} strokeWidth={1.8} />
                  <div className="text-xs font-medium uppercase tracking-wider text-[#2A1810]">{p.title}</div>
                </div>
              ))}
            </div>
            <div className="mt-9 space-y-3 text-sm text-[#5D3A1F]">
              <div className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#6B8E5A] mt-0.5 shrink-0" /><span>We listen to your challenge, uninterrupted.</span></div>
              <div className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#6B8E5A] mt-0.5 shrink-0" /><span>We share how the CCS framework could apply to your situation.</span></div>
              <div className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#6B8E5A] mt-0.5 shrink-0" /><span>You leave with clarity — whether or not we work together next.</span></div>
            </div>
          </div>

          <div className="ccs-card p-6 lg:p-8" data-testid="booking-form-card">
            <h3 className="font-display text-2xl font-semibold text-[#2A1810]">Tell us about your context</h3>
            <p className="text-sm text-[#6B5944] mt-1">Takes about 2 minutes.</p>
            {form.workshop_interest && (
              <div className="mt-4 flex items-center justify-between gap-3 bg-[#C28A2E]/12 border border-[#C28A2E]/35 rounded-xl px-4 py-3" data-testid="workshop-enquiry-banner">
                <div className="text-sm text-[#5D3A1F]">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#9A6B1E] block">Workshop enquiry</span>
                  <span className="font-semibold">{form.workshop_interest}</span>
                </div>
                <button type="button" onClick={() => setF("workshop_interest", "")} className="text-xs text-[#6B5944] hover:text-[#5D3A1F] underline shrink-0" data-testid="workshop-enquiry-clear">
                  General enquiry instead
                </button>
              </div>
            )}
            <form onSubmit={submit} className="space-y-4 mt-6" data-testid="booking-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Full Name *</Label>
                  <Input data-testid="booking-name-input" value={form.full_name} onChange={(e) => setF("full_name", e.target.value)} className="h-11 rounded-lg border-[#EBE3D2] bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Position / Title</Label>
                  <Input data-testid="booking-position-input" value={form.position} onChange={(e) => setF("position", e.target.value)} className="h-11 rounded-lg border-[#EBE3D2] bg-white" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs font-medium">Organization / School / Community Name *</Label>
                  <Input data-testid="booking-org-input" value={form.organization_name} onChange={(e) => setF("organization_name", e.target.value)} className="h-11 rounded-lg border-[#EBE3D2] bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Email *</Label>
                  <Input data-testid="booking-email-input" type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} className="h-11 rounded-lg border-[#EBE3D2] bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Phone / WhatsApp</Label>
                  <Input data-testid="booking-phone-input" value={form.phone} onChange={(e) => setF("phone", e.target.value)} className="h-11 rounded-lg border-[#EBE3D2] bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Sector / Industry</Label>
                  <Input data-testid="booking-industry-input" placeholder="e.g. Credit union, school, community group" value={form.industry} onChange={(e) => setF("industry", e.target.value)} className="h-11 rounded-lg border-[#EBE3D2] bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Approximate group size</Label>
                  <Select value={form.employees_count} onValueChange={(v) => setF("employees_count", v)}>
                    <SelectTrigger data-testid="booking-employees-select" className="h-11 rounded-lg border-[#EBE3D2] bg-white"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1–10</SelectItem>
                      <SelectItem value="11-50">11–50</SelectItem>
                      <SelectItem value="51-200">51–200</SelectItem>
                      <SelectItem value="201-500">201–500</SelectItem>
                      <SelectItem value="500+">500+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Focus areas of interest (select all that apply)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border border-[#EBE3D2] rounded-xl bg-[#FDFBF5] max-h-44 overflow-y-auto">
                  {(ref.services?.length ? ref.services : BOOKING_SERVICES).map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        data-testid={`booking-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        checked={form.services_interested.includes(s)}
                        onCheckedChange={() => toggleService(s)}
                      />
                      <span className="text-[#2A1810]">{s}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">What are you working with? *</Label>
                <Textarea data-testid="booking-challenge-input" rows={4} value={form.business_challenge} onChange={(e) => setF("business_challenge", e.target.value)}
                  placeholder="What challenge are you trying to solve? e.g., team conflict, communication gaps, student leadership, community engagement, service culture…"
                  className="rounded-lg border-[#EBE3D2] bg-white" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Preferred Contact Method</Label>
                <Select value={form.preferred_contact} onValueChange={(v) => setF("preferred_contact", v)}>
                  <SelectTrigger data-testid="booking-contact-select" className="h-11 rounded-lg border-[#EBE3D2] bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Phone">Phone</SelectItem>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {error && <div className="text-sm text-[#A23E3E] bg-[#A23E3E]/10 rounded-lg p-3" data-testid="booking-error">{error}</div>}

              <Button type="submit" disabled={submitting} data-testid="booking-submit-btn"
                className="w-full h-12 rounded-xl bg-[#5D3A1F] hover:bg-[#4A2E18] text-white font-medium text-base mt-2 hover:-translate-y-[1px] hover:shadow-lg transition-all">
                {submitting ? "Sending…" : "Schedule My Consultation"}
              </Button>
              <p className="text-xs text-[#6B5944] text-center mt-2">
                We respect your privacy. Your details are used only to reach out about your consultation.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* ============== Footer ============== */}
      <footer className="bg-[#2A1810] text-white/85">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3">
              <LogoMark size={40} />
              <div>
                <div className="font-display text-base font-semibold text-white">The CCS Approach</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5D88A] font-medium">Consultancy Services</div>
              </div>
            </div>
            <p className="text-sm text-white/70 mt-4 leading-relaxed">
              Empowering people to communicate clearly, collaborate effectively, and serve passionately —
              across workplaces, schools, and communities.
            </p>
            <p className="text-xs text-[#F5D88A]/70 mt-4 italic">
              "Let everything you do be done in love." — 1 Corinthians 16:14
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5D88A] font-bold mb-3">Contact</div>
            <ul className="space-y-2 text-sm">
              <li><a href="mailto:theccsapproach@gmail.com" className="inline-flex items-center gap-2 hover:text-white transition"><Mail className="w-3.5 h-3.5" /> theccsapproach@gmail.com</a></li>
              <li><a href="tel:+18687529924" className="inline-flex items-center gap-2 hover:text-white transition"><Phone className="w-3.5 h-3.5" /> 868 752 9924</a></li>
              <li><a href="https://linkedin.com/in/joylance-chapman-george-91196b4a" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white transition"><Linkedin className="w-3.5 h-3.5" /> LinkedIn</a></li>
            </ul>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5D88A] font-bold mb-3 mt-6">Follow</div>
            <ul className="space-y-2 text-sm">
              <li><a href="https://instagram.com/theccsapproach" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white transition" data-testid="footer-instagram-link"><Instagram className="w-3.5 h-3.5" /> @theccsapproach · Instagram</a></li>
              <li><a href="https://threads.net/@theccsapproach" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white transition" data-testid="footer-threads-link"><MessageSquare className="w-3.5 h-3.5" /> @theccsapproach · Threads</a></li>
              <li><a href="https://tiktok.com/@theccsapproach" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white transition" data-testid="footer-tiktok-link"><Video className="w-3.5 h-3.5" /> @theccsapproach · TikTok</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5D88A] font-bold mb-3">Explore</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/discover" className="hover:text-white transition inline-flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Take the Needs Assessment</Link></li>
              <li><a href="#booking" className="hover:text-white transition inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Book a Consultation</a></li>
              <li><Link to="/testimonials" className="hover:text-white transition inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Client Testimonials</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6">
            <LegalFooter dark />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicBookingPage;
